import { AxiosRequestConfig } from "axios";

import { stationOpsApi } from "../config/apiConfig";
import { getActorEmail } from "../storage/AuthStorage";
import {
  AppliedVariant,
  BlockedStation,
  CatalogueManufacturer,
  Decision,
  EroundCatalogue,
  EroundMappingView,
  ModelMapping,
  ModelMappingInput,
  ModelMappingVersion,
  Outcome,
  ProvisioningDetail,
  ProvisioningPage,
  ProvisioningRequestView,
  ProvisioningSummary,
  SavedModelMapping,
  StandIn,
  ScreenHealthView,
  StationDataView,
  UiDifference,
  UiHealth,
  UnmappedModel,
} from "../types/eroundProvisioning";
import { screenTitle } from "../utils/eroundProvisioning";

// station-ops' eRound provisioning admin API (SOPS-145), as the Back Office
// uses it: specs/013-eround-provisioning-review/contracts/.
//
// The OCPP key never reaches the browser in the clear — station-ops masks it
// as "***" — and the mappers below build every view model field by field, so
// not even the mask is carried over. Failures are logged with the station id
// and HTTP status only, never with a response body.

const BASE = "/internal/eround-provisioning";

const station = (stationId: string) => `${BASE}/${encodeURIComponent(stationId)}`;
const httpStatus = (err: any): number | undefined => err?.response?.status;

const logFailure = (action: string, subject: string, err: any) => {
  console.error(`eRound provisioning: ${action} failed for ${subject} (HTTP ${httpStatus(err) ?? "none"})`);
};

const toSummary = (raw: any): ProvisioningSummary => ({
  stationId: raw.station_id,
  prospectId: raw.prospect_id,
  status: raw.status,
  missing: raw.missing ?? [],
  alreadyInEround: Boolean(raw.already_in_eround),
  attempt: raw.attempt ?? 1,
  updatedAt: raw.updated_at,
  waitingOn: raw.waiting_on ?? null,
  street: raw.street ?? null,
  postcode: raw.postcode ?? null,
  city: raw.city ?? null,
  manualActionHandled: toDecision(raw.manual_action_handled),
});

const toStation = (raw: any): StationDataView => ({
  manufacturer: raw?.manufacturer ?? null,
  model: raw?.model ?? null,
  chargingPoints: raw?.charging_points ?? null,
  locationType: raw?.location_type ?? null,
  street: raw?.street ?? null,
  postcode: raw?.postcode ?? null,
  city: raw?.city ?? null,
  country: raw?.country ?? null,
});

const toStandIn = (raw: any): StandIn | null =>
  raw ? { reason: raw.reason, supportReference: raw.support_reference ?? null } : null;

const toAppliedVariant = (raw: any): AppliedVariant => ({
  modelKey: raw.model_key,
  onboardingManufacturer: raw.onboarding_manufacturer,
  onboardingModel: raw.onboarding_model,
  stationIds: raw.station_ids ?? [],
});

const toMapping = (raw: any): EroundMappingView | null =>
  raw
    ? {
        manufacturer: raw.manufacturer,
        model: raw.model,
        chargingPoints: raw.charging_points,
        source: raw.source,
        setBy: raw.set_by,
        setAt: raw.set_at,
        standIn: toStandIn(raw.stand_in),
      }
    : null;

const toVersion = (raw: any): ModelMappingVersion => ({
  eroundManufacturer: raw.eround_manufacturer,
  eroundModel: raw.eround_model,
  chargingPoints: raw.charging_points,
  source: raw.source,
  setBy: raw.set_by,
  setAt: raw.set_at,
  standIn: toStandIn(raw.stand_in),
});

const toModelMapping = (raw: any): ModelMapping => ({
  ...toVersion(raw),
  modelKey: raw.model_key,
  onboardingManufacturer: raw.onboarding_manufacturer,
  onboardingModel: raw.onboarding_model,
  history: (raw.history ?? []).map(toVersion),
  appliedTo: (raw.applied_to ?? []).map(toAppliedVariant),
});

const toDecision = (raw: any): Decision | null =>
  raw
    ? { actorId: raw.actor_id, actorEmail: raw.actor_email ?? null, at: raw.at, reason: raw.reason ?? null }
    : null;

const toOutcome = (raw: any): Outcome | null =>
  raw
    ? {
        status: raw.status,
        reason: raw.reason ?? null,
        eroundLocationId: raw.eround_location_id ?? null,
        eroundStationId: raw.eround_station_id ?? null,
        finishedAt: raw.finished_at ?? null,
      }
    : null;

const toRequest = (raw: any): ProvisioningRequestView | null => {
  const location = raw?.location;
  const stationPart = raw?.station;
  if (!location || !stationPart) return null;
  return {
    location: {
      locationName: location.location_name,
      locationType: location.location_type,
      address: location.address,
      street: location.street,
      postcode: location.postcode,
      country: location.country,
      latitude: location.latitude ?? null,
      longitude: location.longitude ?? null,
      publiclyVisible: Boolean(location.publicly_visible),
    },
    station: {
      location: stationPart.location,
      stationName: stationPart.station_name,
      manufacturer: stationPart.manufacturer,
      model: stationPart.model,
      numberOfChargingPoints: stationPart.number_of_charging_points,
      evseDetails: (stationPart.evse_details ?? []).map((evse: any) => ({
        evseId: evse.evse_id,
        connectionType: evse.connection_type ?? null,
        currentType: evse.current_type ?? null,
        maxPowerKw: evse.max_power_kw ?? null,
      })),
    },
  };
};

const toDetail = (raw: any): ProvisioningDetail => ({
  ...toSummary(raw),
  request: toRequest(raw.request),
  fingerprint: raw.fingerprint ?? null,
  ocppSecretPresent: Boolean(raw.ocpp_secret_present),
  contractSignedAt: raw.contract_signed_at ?? null,
  approval: toDecision(raw.approval),
  rejection: toDecision(raw.rejection),
  lastResult: toOutcome(raw.last_result),
  manualActionReason: raw.manual_action_reason ?? null,
  station: toStation(raw.station),
  eroundMapping: toMapping(raw.eround_mapping),
  chargingNetwork: toDecision(raw.charging_network),
});

/** One station's record, or null when station-ops does not track the station. */
export const getProvisioning = async (stationId: string): Promise<ProvisioningDetail | null> => {
  try {
    const response = await stationOpsApi.get(station(stationId));
    return toDetail(response.data);
  } catch (err) {
    if (httpStatus(err) === 404) return null;
    logFailure("reading the record", stationId, err);
    throw err;
  }
};

/**
 * Records in one status, one page at a time. station-ops reads a single
 * `status` per call, so a list across statuses is one call per status.
 */
export const listProvisioning = async ({
  status,
  cursor,
  limit = 50,
}: {
  status: string;
  cursor?: string | null;
  limit?: number;
}): Promise<ProvisioningPage> => {
  try {
    const response = await stationOpsApi.get(BASE, {
      params: { status, limit, ...(cursor ? { cursor } : {}) },
    });
    return {
      records: (response.data?.content ?? []).map(toSummary),
      nextCursor: response.data?.next_cursor ?? null,
    };
  } catch (err) {
    logFailure("listing records", status, err);
    throw err;
  }
};

// Names the member of staff on every write. Back Office access tokens carry no email, so station-ops shows this
// address in "Approved by", "Marked by" and the like; the token subject stays the identifier (SOPS-183).
const actor = (): [] | [AxiosRequestConfig] => {
  const email = getActorEmail();
  return email ? [{ headers: { "X-Actor-Email": email } }] : [];
};

const act = async (stationId: string, action: string, body?: object): Promise<ProvisioningDetail> => {
  try {
    const path = `${station(stationId)}/${action}`;
    const named = actor();
    const response =
      body === undefined && named.length === 0
        ? await stationOpsApi.post(path)
        : await stationOpsApi.post(path, body, ...named);
    return toDetail(response.data);
  } catch (err) {
    logFailure(action, stationId, err);
    throw err;
  }
};

/** Sends exactly the reviewed request to eRound: the fingerprint ties the approval to it. */
export const approveProvisioning = (stationId: string, fingerprint: string) =>
  act(stationId, "approve", { fingerprint });

/** Declines the request; the station falls back to the manual eRound path. */
export const rejectProvisioning = (stationId: string, reason: string) => act(stationId, "reject", { reason });

/** Sends a failed station again, as the next attempt. */
export const retryProvisioning = (stationId: string) => act(stationId, "retry");

/** Records that support did the station's manual step; it stays "Needs manual action" but no longer waits on support. */
export const markProvisioningHandled = (stationId: string) => act(stationId, "handled");

/** Marks or unmarks a Ladeverbund (master/slave): one station with all its charging points (SOPS-174). */
export const setChargingNetwork = async (stationId: string, enabled: boolean): Promise<ProvisioningDetail> => {
  try {
    const response = await stationOpsApi.put(`${station(stationId)}/charging-network`, { enabled }, ...actor());
    return toDetail(response.data);
  } catch (err) {
    logFailure("marking a Ladeverbund", stationId, err);
    throw err;
  }
};

/** Onboarding wallbox models that block a station because eRound's names are not recorded yet. */
export const listUnmappedModels = async (): Promise<UnmappedModel[]> => {
  try {
    const response = await stationOpsApi.get(`${BASE}/model-mappings/unmapped`);
    return (response.data?.content ?? []).map((raw: any) => {
      const blockedStationIds: string[] = raw.blocked_station_ids ?? [];
      // An older station-ops sends only the IDs; the stations then have no known prospect.
      const blockedStations: BlockedStation[] = raw.blocked_stations
        ? raw.blocked_stations.map((s: any) => ({ stationId: s.station_id, prospectId: s.prospect_id ?? null }))
        : blockedStationIds.map((stationId) => ({ stationId, prospectId: null }));
      return {
        modelKey: raw.model_key,
        onboardingManufacturer: raw.onboarding_manufacturer,
        onboardingModel: raw.onboarding_model,
        blockedStationIds,
        blockedStations,
      };
    });
  } catch (err) {
    logFailure("listing unmapped models", "all", err);
    throw err;
  }
};

/** Every saved model mapping, all pages, with stand-in decisions and their history (SOPS-181). */
export const listModelMappings = async (): Promise<ModelMapping[]> => {
  try {
    const all: ModelMapping[] = [];
    let cursor: string | null = null;
    do {
      const response = await stationOpsApi.get(`${BASE}/model-mappings`, {
        params: { limit: 500, ...(cursor && { cursor }) },
      });
      all.push(...(response.data?.content ?? []).map(toModelMapping));
      cursor = response.data?.next_cursor ?? null;
    } while (cursor);
    return all;
  } catch (err) {
    logFailure("listing model mappings", "all", err);
    throw err;
  }
};

/** eRound's catalogue for this environment's tenant, or null until the worker has sent one (SOPS-180). */
export const getEroundCatalogue = async (): Promise<EroundCatalogue | null> => {
  try {
    const response = await stationOpsApi.get(`${BASE}/catalogue`);
    const raw = response.data ?? {};
    return {
      tenant: raw.tenant,
      exportedAt: raw.exported_at,
      receivedAt: raw.received_at ?? null,
      manufacturers: (raw.manufacturers ?? []).map(
        (m: any): CatalogueManufacturer => ({
          name: m.name,
          marking: m.marking ?? null,
          ocpp16CoreTested: Boolean(m.ocpp16_core_tested),
          ocpp16SmartChargingTested: Boolean(m.ocpp16_smart_charging_tested),
          models: (m.models ?? []).map((model: any) => ({
            name: model.name,
            integrationStatus: model.integration_status ?? null,
            connectors: (model.connectors ?? []).map((c: any) => ({
              current: c.current,
              connectorType: c.connector_type ?? null,
              maxPowerKw: c.max_power_kw ?? null,
              phases: c.phases ?? null,
            })),
          })),
        })
      ),
    };
  } catch (err) {
    if (httpStatus(err) === 404) return null;
    logFailure("loading eRound's catalogue", "all", err);
    throw err;
  }
};

/** Records eRound's names for an onboarding model; every station with it is re-evaluated. */
export const saveModelMapping = async (
  model: UnmappedModel,
  input: ModelMappingInput
): Promise<SavedModelMapping> => {
  try {
    const response = await stationOpsApi.put(
      `${BASE}/model-mappings/${encodeURIComponent(model.modelKey)}`,
      {
        onboarding_manufacturer: model.onboardingManufacturer,
        onboarding_model: model.onboardingModel,
        eround_manufacturer: input.eroundManufacturer.trim(),
        eround_model: input.eroundModel.trim(),
        charging_points: input.chargingPoints,
        ...(input.standIn && {
          stand_in: {
            reason: input.standIn.reason.trim(),
            ...(input.standIn.supportReference.trim() && { support_reference: input.standIn.supportReference.trim() }),
          },
        }),
      },
      ...actor()
    );
    return {
      modelKey: response.data?.model_key ?? model.modelKey,
      reevaluatedStationIds: response.data?.reevaluated_station_ids ?? [],
    };
  } catch (err) {
    logFailure("saving the model mapping", model.modelKey, err);
    throw err;
  }
};

const toDifference = (raw: any): UiDifference => ({
  element: raw.element,
  attribute: raw.attribute,
  old: raw.old ?? null,
  new: raw.new ?? null,
  severity: raw.severity,
  candidates: raw.candidates ?? [],
});

const toScreenHealth = (raw: any): ScreenHealthView => ({
  screen: raw.screen,
  title: screenTitle(raw.screen),
  status: raw.status,
  since: raw.since ?? null,
  lastCheckedAt: raw.last_checked_at ?? null,
  reason: raw.reason ?? null,
  differences: (raw.differences ?? []).map(toDifference),
});

/** The latest check of each eRound screen the automation uses (SOPS-164). */
export const getUiHealth = async (): Promise<UiHealth> => {
  try {
    const { data } = await stationOpsApi.get("/internal/eround-ui-health");
    return {
      tenant: data?.tenant,
      checked: Boolean(data?.checked),
      lastCheckedAt: data?.last_checked_at ?? null,
      lastSource: data?.last_source ?? null,
      outcome: data?.outcome ?? null,
      guardMode: data?.guard_mode ?? null,
      workerVersion: data?.worker_version ?? null,
      screens: (data?.screens ?? []).map(toScreenHealth),
    };
  } catch (err) {
    logFailure("reading screen health", "the default tenant", err);
    throw err;
  }
};
